import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  composePhonePayload,
  maskCPF,
  maskCEP,
  maskPhoneBR,
  onlyDigits,
  parseStoredPhone,
  validateRepresentativeForm,
} from "../lib/representatives";
import { fetchCepAddress, mergeCepAddress, normalizeCep } from "../lib/cep";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Loader2, Lock, Plus, Save } from "lucide-react";
import { toast } from "sonner";

const UF_LIST = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];

const EMPTY_FORM = {
  full_name: "", document: "", email: "",
  phone: "", phone_ddi: "", country: "BR",
  zip_code: "", street: "", street_number: "", complement: "",
  neighborhood: "", city: "", state: "",
  is_default_signer: false,
};

/**
 * Dialog de cadastro/edição de representante/sócio do cedente (item 22).
 *
 * Reutilizado pelo detalhe do cedente (Clients.jsx) e pelo atalho "completar
 * cadastro" da etapa Assinantes do wizard (NewOperation.jsx).
 *
 * - Criação: todos os campos editáveis.
 * - Edição: CPF TRAVADO (cadeado + hint) — trocar documento = trocar
 *   identidade: arquive e recadastre (regra da V2; PATCH com documento → 422).
 * - Validações client-side são ESPELHO das da V2 (feedback imediato); o erro
 *   da V2 (ex.: 409 CPF já ativo) é sempre exibido com clareza.
 * - Obrigatoriedade (owner 2026-07-11): TODOS os campos obrigatórios EXCETO
 *   Complemento (país tem default BR) — a V2 devolve 422 fail-closed para o
 *   mesmo conjunto (o front valida antes por UX; o back é a autoridade).
 * - Telefone: máscara dinâmica BR (xx) xxxxx-xxxx / (xx) xxxx-xxxx (usuário
 *   digita só DDD+número); país != BR → DDI editável + dígitos livres. O
 *   payload vai como exibido (campo ÚNICO livre na V2 — ver representatives.js).
 * - CEP-first: cada CEP completo (8 dígitos) DIFERENTE do último consultado
 *   re-busca a ViaCEP e re-preenche rua/bairro/cidade/UF (campos digitados
 *   manualmente nesta sessão nunca são sobrescritos — mergeCepAddress), e o
 *   foco pula para o Número. CEP inexistente/rede fora → hint discreto, form
 *   segue manual, submit nunca é bloqueado pela consulta.
 */
export default function RepresentativeFormDialog({
  open,
  onOpenChange,
  cedenteId,
  // Base dos endpoints de representante. Default = proxy do PARCEIRO
  // (`/v2/cedentes/{id}`); o wizard no modo cedente passa `/cedente` (o id do
  // cedente vem do token, então não vai no path). Só a base muda — shape do
  // payload e mensagens de erro são as mesmas nos dois ramos.
  basePath,
  representative = null, // null = criar; objeto = editar
  onSaved,
}) {
  const editing = !!representative;
  const base = basePath || `/v2/cedentes/${cedenteId}`;
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Autofill por CEP: "idle" | "loading" | "not_found".
  const [cepStatus, setCepStatus] = useState("idle");
  const streetNumberRef = useRef(null); // foco pós-autofill
  const cepRequestIdRef = useRef(0); // descarta resposta obsoleta (troca de CEP/reopen)
  const lastLookupCepRef = useRef(null); // evita re-consultar o mesmo CEP
  const manuallyEditedRef = useRef(new Set()); // campos digitados NESTA sessão do form

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setApiError(null);
    setCepStatus("idle");
    cepRequestIdRef.current += 1; // invalida consulta de CEP em voo
    lastLookupCepRef.current = null;
    manuallyEditedRef.current = new Set();
    if (representative) {
      // Telefone armazenado é campo único livre — desmonta p/ os inputs
      // (BR legado com +55 embutido perde o prefixo na exibição; o submit
      // regrava no formato novo).
      const parsedPhone = parseStoredPhone(representative.phone, representative.country);
      setForm({
        full_name: representative.full_name || "",
        document: maskCPF(representative.document_number || ""),
        email: representative.email || "",
        phone: parsedPhone.phone,
        phone_ddi: parsedPhone.phone_ddi,
        country: parsedPhone.country,
        zip_code: maskCEP(representative.zip_code || ""),
        street: representative.street || "",
        street_number: representative.street_number || "",
        complement: representative.complement || "",
        neighborhood: representative.neighborhood || "",
        city: representative.city || "",
        state: representative.state || "",
        is_default_signer: !!representative.is_default_signer,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, representative]);

  const set = (field, value) => {
    manuallyEditedRef.current.add(field);
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  };

  // CEP-first: 8 dígitos completos e DIFERENTES do último consultado →
  // consulta ViaCEP e re-preenche o endereço (campos digitados manualmente
  // nesta sessão nunca são sobrescritos — regras em mergeCepAddress; tudo
  // segue editável). Falha/CEP inexistente → hint discreto, form segue manual.
  const handleCepChange = async (raw) => {
    const masked = maskCEP(raw);
    set("zip_code", masked);
    const digits = normalizeCep(masked);
    if (digits.length !== 8) {
      lastLookupCepRef.current = null;
      setCepStatus("idle");
      return;
    }
    if (digits === lastLookupCepRef.current) return;
    lastLookupCepRef.current = digits;
    const requestId = ++cepRequestIdRef.current;
    setCepStatus("loading");
    const address = await fetchCepAddress(digits);
    if (requestId !== cepRequestIdRef.current) return; // resposta obsoleta
    if (!address) {
      setCepStatus("not_found");
      return;
    }
    setCepStatus("idle");
    setForm((f) => mergeCepAddress(f, address, { manuallyEdited: manuallyEditedRef.current }));
    streetNumberRef.current?.focus();
  };

  // Troca de país: normaliza o código (2 letras maiúsculas) e re-apresenta o
  // telefone na convenção do destino (BR = máscara DDD+número; outro = dígitos
  // livres + DDI). Os dígitos digitados são preservados.
  const handleCountryChange = (raw) => {
    const code = String(raw || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2);
    setForm((f) => ({
      ...f,
      country: code,
      phone: code === "BR" ? maskPhoneBR(f.phone) : onlyDigits(f.phone).slice(0, 15),
    }));
    setErrors((e) => ({ ...e, country: undefined, phone: undefined, phone_ddi: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const localErrors = validateRepresentativeForm(form, { documentEditable: !editing });
    setErrors(localErrors);
    if (Object.values(localErrors).some(Boolean)) return;

    setSubmitting(true);
    setApiError(null);
    // Complemento é o ÚNICO opcional: "" vira omissão/null (a V2 espera
    // ausência, não string vazia). Os demais são obrigatórios e já passaram
    // na validação acima (não-vazios).
    const optional = (v) => (String(v || "").trim() === "" ? null : v.trim());
    // Telefone: campo único livre na V2 — vai formatado como exibido
    // (BR "(11) 99999-9999"; outro país "+DDI dígitos").
    const required = {
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      phone: composePhonePayload(form),
      zip_code: onlyDigits(form.zip_code),
      street: form.street.trim(),
      street_number: form.street_number.trim(),
      neighborhood: form.neighborhood.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      country: String(form.country || "BR").trim().toUpperCase(),
      is_default_signer: form.is_default_signer,
    };
    try {
      if (editing) {
        // PATCH: SEM campo de documento (travado na V2 — arquive e recadastre).
        await api.patch(
          `${base}/representatives/${representative.id}`,
          { ...required, complement: optional(form.complement) }
        );
        toast.success("Representante atualizado");
      } else {
        const body = { ...required, document: onlyDigits(form.document) };
        const complement = optional(form.complement);
        if (complement != null) body.complement = complement;
        await api.post(`${base}/representatives`, body);
        toast.success("Representante cadastrado");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      // Erro da V2 exibido com fidelidade (ex.: 409 "já existe representante
      // ATIVO deste cedente com o mesmo CPF"; 422 anti-injeção) — nunca
      // sucesso silencioso.
      setApiError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="representative-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar Representante" : "Cadastrar Representante"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Dados de contato, endereço e a flag de assinante padrão podem ser editados."
              : "Sócio/representante que pode assinar contratos pelo cedente."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Nome completo *</Label>
              <Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Maria da Silva" data-testid="rep-full-name" />
              {errors.full_name && <p className="text-[12px] text-red-600" role="alert">{errors.full_name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                CPF * {editing && <Lock className="h-3 w-3 text-zinc-400" />}
              </Label>
              <Input
                required
                value={form.document}
                onChange={(e) => set("document", maskCPF(e.target.value))}
                placeholder="000.000.000-00"
                maxLength={14}
                className="font-mono-num"
                disabled={editing}
                data-testid="rep-document"
              />
              {editing ? (
                <p className="text-[12px] text-zinc-400">
                  Documento não editável — arquive e recadastre para trocar o CPF.
                </p>
              ) : (
                errors.document && <p className="text-[12px] text-red-600" role="alert">{errors.document}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>E-mail *</Label>
              <Input required type="text" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="maria@empresa.com" data-testid="rep-email" />
              <p className="text-[11px] text-zinc-400">Canal de assinatura do contrato (ZapSign).</p>
              {errors.email && <p className="text-[12px] text-red-600" role="alert">{errors.email}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Telefone *</Label>
              {form.country === "BR" ? (
                <Input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", maskPhoneBR(e.target.value))}
                  placeholder="(11) 99999-9999"
                  // SEM maxLength: cortaria a colagem de "+55 11 99999-9999"
                  // (17 chars) ANTES do onChange e o strip do DDI não veria os
                  // 12/13 dígitos — a máscara já trava a exibição em 11 díg.
                  className="font-mono-num"
                  data-testid="rep-phone"
                />
              ) : (
                <div className="flex gap-2">
                  <div className="relative w-20 shrink-0">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-zinc-400" aria-hidden="true">+</span>
                    <Input
                      required
                      value={form.phone_ddi}
                      onChange={(e) => set("phone_ddi", onlyDigits(e.target.value).slice(0, 3))}
                      placeholder="351"
                      className="pl-6 font-mono-num"
                      aria-label="DDI do país"
                      data-testid="rep-phone-ddi"
                    />
                  </div>
                  <Input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set("phone", onlyDigits(e.target.value).slice(0, 15))}
                    placeholder="912345678"
                    className="font-mono-num"
                    data-testid="rep-phone"
                  />
                </div>
              )}
              <p className="text-[11px] text-zinc-400">
                {form.country === "BR"
                  ? "Só DDD + número — Brasil (+55) é o padrão."
                  : "DDI do país + número (só dígitos)."}
              </p>
              {errors.phone_ddi && <p className="text-[12px] text-red-600" role="alert">{errors.phone_ddi}</p>}
              {errors.phone && <p className="text-[12px] text-red-600" role="alert">{errors.phone}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>CEP *</Label>
              <div className="relative">
                <Input
                  required
                  value={form.zip_code}
                  onChange={(e) => handleCepChange(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  className="font-mono-num"
                  aria-busy={cepStatus === "loading"}
                  data-testid="rep-zip"
                />
                {cepStatus === "loading" && (
                  <Loader2 className="h-4 w-4 animate-spin text-zinc-400 absolute right-2.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
                )}
              </div>
              <p aria-live="polite" className="text-[12px] text-zinc-400" data-testid="rep-zip-hint">
                {cepStatus === "loading" && "Buscando endereço pelo CEP…"}
                {cepStatus === "not_found" && "CEP não encontrado — preencha manualmente."}
              </p>
              {errors.zip_code && <p className="text-[12px] text-red-600" role="alert">{errors.zip_code}</p>}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Rua *</Label>
              <Input required value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="Av. Paulista" data-testid="rep-street" />
              {errors.street && <p className="text-[12px] text-red-600" role="alert">{errors.street}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Número *</Label>
              <Input required ref={streetNumberRef} value={form.street_number} onChange={(e) => set("street_number", e.target.value)} placeholder="1000" data-testid="rep-street-number" />
              {errors.street_number && <p className="text-[12px] text-red-600" role="alert">{errors.street_number}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Complemento <span className="font-normal text-zinc-400">(opcional)</span></Label>
              <Input value={form.complement} onChange={(e) => set("complement", e.target.value)} placeholder="Sala 101" />
              {errors.complement && <p className="text-[12px] text-red-600" role="alert">{errors.complement}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Bairro *</Label>
              <Input required value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} placeholder="Bela Vista" data-testid="rep-neighborhood" />
              {errors.neighborhood && <p className="text-[12px] text-red-600" role="alert">{errors.neighborhood}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Cidade *</Label>
              <Input required value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="São Paulo" data-testid="rep-city" />
              {errors.city && <p className="text-[12px] text-red-600" role="alert">{errors.city}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>UF *</Label>
              <Select value={form.state} onValueChange={(v) => set("state", v)}>
                <SelectTrigger data-testid="rep-state"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {UF_LIST.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.state && <p className="text-[12px] text-red-600" role="alert">{errors.state}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>País</Label>
              <Input
                value={form.country}
                onChange={(e) => handleCountryChange(e.target.value)}
                placeholder="BR"
                maxLength={2}
                className="font-mono-num uppercase"
                data-testid="rep-country"
              />
              <p className="text-[11px] text-zinc-400">Código de 2 letras (padrão BR).</p>
              {errors.country && <p className="text-[12px] text-red-600" role="alert">{errors.country}</p>}
            </div>
            <div className="sm:col-span-2 flex items-start gap-2 p-3 rounded-md border border-[var(--zemo-border)] bg-zinc-50/50">
              <Checkbox
                id="rep-default-signer"
                checked={form.is_default_signer}
                onCheckedChange={(v) => set("is_default_signer", !!v)}
                data-testid="rep-default-signer"
              />
              <div>
                <label htmlFor="rep-default-signer" className="text-sm font-medium text-zinc-900 cursor-pointer">
                  Assinante padrão
                </label>
                <p className="text-[12px] text-zinc-500">
                  Pré-selecionado como assinante do contrato nas novas operações
                  (ajustável operação a operação).
                </p>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-zinc-400">
            Todos os campos marcados com * são obrigatórios — apenas o
            Complemento é opcional. Os dados alimentam a qualificação do
            representante no <strong>contrato de assinatura</strong>.
          </p>

          {apiError && (
            <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800" data-testid="rep-api-error">
              {apiError}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={submitting} data-testid="rep-submit">
              {submitting
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : editing ? <Save className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {editing ? "Salvar alterações" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
